'use client';

import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

export type RecentSearch = {
  locationSection: { value?: string[] | string };
  parameters: { [key: string]: string };
};

export type RecentSearchInput = Omit<RecentSearch, 'parameters'> & {
  parameters: URLSearchParams;
};

export interface RecentSearchesContextState {
  addRecentSearch: (search: RecentSearchInput) => void;
  recentSearches: RecentSearch[];
  trimRecentSearches: (maxEntries: number) => void;
}

const RecentSearchesContext = createContext<
  RecentSearchesContextState | undefined
>(undefined);

const getLocalStorage = (key: string) => {
  if (typeof window === 'undefined') return [];

  const item = localStorage.getItem(key);

  return item ? JSON.parse(item) : [];
};

const setLocalStorage = (key: string, value: RecentSearch[]) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem(key, JSON.stringify(value));
  }
};

export const RecentSearchesProvider = ({
  children,
  initialData,
}: {
  children: ReactNode;
  initialData?: RecentSearchesContextState;
}) => {
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>(
    () => initialData?.recentSearches || getLocalStorage('recentSearches'),
  );

  useEffect(() => {
    setLocalStorage('recentSearches', recentSearches);
  }, [recentSearches]);

  const addRecentSearch = ({
    parameters,
    locationSection,
  }: RecentSearchInput) => {
    setRecentSearches(previousSearches => {
      const parametersString = parameters.toString();

      const isExistingSearch = previousSearches.some(existingSearch => {
        const existingSearchParams = new URLSearchParams(
          existingSearch.parameters,
        );

        return existingSearchParams.toString() === parametersString;
      });

      return isExistingSearch
        ? previousSearches
        : [
            {
              locationSection,
              parameters: Object.fromEntries(parameters.entries()),
            },
            ...previousSearches,
          ];
    });
  };

  const trimRecentSearches = (maxEntries: number) => {
    setRecentSearches(previousSearches => {
      return previousSearches.length > maxEntries
        ? previousSearches.slice(0, maxEntries)
        : previousSearches;
    });
  };

  return (
    <RecentSearchesContext.Provider
      value={{
        addRecentSearch,
        recentSearches,
        trimRecentSearches,
      }}
    >
      {children}
    </RecentSearchesContext.Provider>
  );
};

export const useRecentSearchesContext = () => {
  const context = useContext(RecentSearchesContext);

  if (!context) {
    throw new Error(
      'useRecentSearchesContext must be used within a RecentSearchesProvider',
    );
  }

  return context;
};
